import { CancelOutcomeSchema, DeleteAccountOutcomeSchema, findFieldDef, } from '@wit-support/pipeline';
import { PIPELINE_DELAY_LABEL_RU, PIPELINE_FIELD_OPERATOR_LABEL_RU, PIPELINE_FIELD_SOURCE_LABEL_RU, pipelineNodeTypeLabelRu, pipelineOutcomeLabelRu, statusLabelRu, } from '@wit-support/ui-domain';
// The pure entry of the kit: the core stays free of React.
import { pluralRu } from '@wit-support/ui/format';
const NO_CONTEXT_KEYS = [];
const noContextKeys = () => NO_CONTEXT_KEYS;
const noSplittable = () => [];
/** The defaults of a type with no optional map, nothing to split and no context writes. */
const PLAIN_OUTCOMES = {
    optionalOutcomeMap: false,
    splittableOutcomes: noSplittable,
    writesContextKeys: noContextKeys,
};
// ---------------------------------------------------------------------------
// Shared words
const LABEL_TRUE = 'Да';
const LABEL_FALSE = 'Нет';
const LABEL_NEXT = 'Далее';
const LABEL_SEPARATOR = ' · ';
const outcomeKey = (o) => `outcome:${o}`;
const parseOutcomeKey = (key) => key.startsWith('outcome:') ? key.slice('outcome:'.length) : null;
const invalidSlot = (node, key) => new Error(`node '${node.id}' (type '${node.type}') has no slot '${key}'`);
const quote = (s) => `«${s}»`;
const MAX_INLINE_TEXT = 40;
const clip = (s) => s.length > MAX_INLINE_TEXT ? `${s.slice(0, MAX_INLINE_TEXT - 1)}…` : s;
const templateSummary = (id, ctx) => {
    if (id === '')
        return 'Шаблон не выбран';
    const name = ctx.templateName(id)?.trim();
    return name ? `Шаблон ${quote(name)}` : 'Шаблон не найден';
};
const withDelay = (head, delay) => delay ? `${head}${LABEL_SEPARATOR}${PIPELINE_DELAY_LABEL_RU[delay] ?? delay}` : head;
const durationRu = (hours) => hours % 24 === 0
    ? `${hours / 24} ${pluralRu(hours / 24, ['дня', 'дней', 'дней'])}`
    : `${hours} ${pluralRu(hours, ['часа', 'часов', 'часов'])}`;
const formatValue = (value) => {
    if (value === undefined)
        return '';
    if (Array.isArray(value))
        return value.map(String).join(', ');
    if (typeof value === 'boolean')
        return value ? 'да' : 'нет';
    if (typeof value === 'number')
        return String(value);
    return quote(clip(value));
};
const VALUELESS_OPERATORS = new Set([
    'exists',
    'not_exists',
    'is_true',
    'is_false',
]);
const fieldMatchSummary = (node) => {
    const source = PIPELINE_FIELD_SOURCE_LABEL_RU[node.source] ?? node.source;
    if (node.fieldPath === '')
        return `${source}${LABEL_SEPARATOR}поле не выбрано`;
    const field = findFieldDef(node.source, node.fieldPath)?.label ?? node.fieldPath;
    const op = PIPELINE_FIELD_OPERATOR_LABEL_RU[node.operator] ?? node.operator;
    const value = VALUELESS_OPERATORS.has(node.operator) ? '' : formatValue(node.value);
    return `${source}${LABEL_SEPARATOR}${field} ${op}${value ? ` ${value}` : ''}`;
};
const conditionSlots = (node) => [
    { key: 'true', labelRu: LABEL_TRUE, target: node.onTrue, effect: 'explicit', editable: true },
    { key: 'false', labelRu: LABEL_FALSE, target: node.onFalse, effect: 'explicit', editable: true },
];
const setConditionSlot = (node, key, target) => {
    if (key === 'true')
        return { node: { ...node, onTrue: target }, behaviorChanges: [] };
    if (key === 'false')
        return { node: { ...node, onFalse: target }, behaviorChanges: [] };
    throw invalidSlot(node, key);
};
const conditionStepSlot = (_node, status) => status === 'branch_true' ? 'true' : status === 'branch_false' ? 'false' : null;
const never = () => false;
const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** What a parameterless check actually asks — the card's second line. */
const PREDICATE_HINT_RU = {
    user_identified: 'Отправитель привязан к аккаунту приложения',
    has_active_subscription: 'У клиента сейчас действует подписка',
};
const predicateSpec = (type) => ({
    kind: 'condition',
    category: 'check',
    labelRu: pipelineNodeTypeLabelRu(type),
    outcomeKeys: [],
    slots: conditionSlots,
    setSlotTarget: setConditionSlot,
    summaryRu: () => null,
    hintRu: PREDICATE_HINT_RU[type],
    create: (id) => ({ id, kind: 'condition', type, onTrue: null, onFalse: null }),
    slotForStepStatus: conditionStepSlot,
    settlesDialog: never,
    ...PLAIN_OUTCOMES,
});
const nextSlots = (node) => [
    { key: 'next', labelRu: LABEL_NEXT, target: node.next, effect: 'explicit', editable: true },
];
const setNextSlot = (node, key, target) => {
    if (key !== 'next')
        throw invalidSlot(node, key);
    return { node: { ...node, next: target }, behaviorChanges: [] };
};
/**
 * `ok` continues to `next`. `skipped` is either a D2-suppressed action (the
 * executor set `result.suppressed`; the walk continues to `next`) or a
 * merge-cancelled run (`result.reason = 'run_cancelled'`, the walk stops) — so it
 * maps to `next` only when `result` proves the suppression. Without `result` it
 * stays unmapped: a wrong arrow count is worse than none.
 */
const nextStepSlot = (_node, status, result) => {
    if (status === 'ok')
        return 'next';
    if (status === 'skipped' && isRecord(result) && typeof result.suppressed === 'string') {
        return 'next';
    }
    return null;
};
const fixedOutcomeSpecParts = (type, outcomes) => {
    const keys = outcomes.map((o) => `outcome:${o}`);
    const slots = (node) => outcomes.map((o) => ({
        key: `outcome:${o}`,
        labelRu: pipelineOutcomeLabelRu(type, o),
        target: node.outcomes[o] ?? null,
        effect: 'explicit',
        editable: true,
    }));
    const setSlotTarget = (node, key, target) => {
        const outcome = parseOutcomeKey(key);
        if (outcome === null || !outcomes.includes(outcome)) {
            throw invalidSlot(node, key);
        }
        return {
            node: { ...node, outcomes: { ...node.outcomes, [outcome]: target } },
            behaviorChanges: [],
        };
    };
    /**
     * `outcome_<x>` → `outcome:<x>`. A `failed` step is mapped to the `escalated`
     * exit when the node declares one: that is the engine rule
     * `escalationTargetForFailedStep` (`runner.ts`) — a failed node with an
     * `escalated` edge continues there.
     */
    const slotForStepStatus = (node, status) => {
        if (status.startsWith('outcome_')) {
            const outcome = status.slice('outcome_'.length);
            return outcomes.includes(outcome)
                ? `outcome:${outcome}`
                : null;
        }
        if (status === 'failed') {
            const escalated = node.outcomes.escalated;
            return typeof escalated === 'string' && escalated.length > 0 ? 'outcome:escalated' : null;
        }
        return null;
    };
    return { outcomeKeys: keys, slots, setSlotTarget, slotForStepStatus };
};
const COLLECT_INPUT_OUTCOMES = [
    'collected',
    'escalated',
];
const RESOLVE_IDENTITY_OUTCOMES = [
    'resolved',
    'unresolved',
];
/** Read access to a cancel/delete `outcomes` map regardless of which of the two it is. */
const routedMap = (node) => node.outcomes;
const routedSpecParts = (type, outcomes, stopOutcomes) => {
    const isStop = (o) => stopOutcomes.includes(o);
    const label = (o) => pipelineOutcomeLabelRu(type, o);
    const successOutcomes = outcomes.filter((o) => !isStop(o));
    const slots = (node) => {
        const map = routedMap(node);
        if (map === undefined) {
            return [
                {
                    key: 'next',
                    labelRu: successOutcomes.map(label).join(LABEL_SEPARATOR),
                    target: node.next,
                    effect: 'explicit',
                    editable: true,
                },
                ...stopOutcomes.map((o) => ({
                    key: outcomeKey(o),
                    labelRu: label(o),
                    target: null,
                    effect: 'stops_run',
                    // Pinning this one materialises the map; any OTHER stop outcome
                    // would then lose its stop.
                    editable: stopOutcomes.length === 1,
                })),
            ];
        }
        return outcomes.map((o) => {
            const mapped = Object.prototype.hasOwnProperty.call(map, o);
            return {
                key: outcomeKey(o),
                labelRu: label(o),
                target: mapped ? (map[o] ?? null) : node.next,
                effect: mapped ? 'explicit' : 'falls_to_next',
                editable: true,
            };
        });
    };
    /**
     * - `next` → sets `node.next` (in the map regime: the fallback of every
     *   unmapped outcome).
     * - `outcome:<x>` with a map → sets that key.
     * - `outcome:<x>` WITHOUT a map → creates the map. Success outcomes stay
     *   unmapped and keep falling to `next` — identical behaviour. Stop outcomes
     *   other than `<x>` CANNOT keep stopping: a map has no «stop as failed» value,
     *   and leaving them unmapped would send a failed cancel into `next` (the §2
     *   п.4 trap). They are pinned to `null` (end of branch — no further side
     *   effect, the step still records `outcome_failed`, which the rail flags as an
     *   error) and reported in `behaviorChanges`: the run now ends `completed`
     *   instead of `failed`. The UI must show that before committing.
     */
    const setSlotTarget = (node, key, target) => {
        if (key === 'next')
            return { node: { ...node, next: target }, behaviorChanges: [] };
        const outcome = parseOutcomeKey(key);
        if (outcome === null || !outcomes.includes(outcome)) {
            throw invalidSlot(node, key);
        }
        const map = routedMap(node);
        if (map !== undefined) {
            return { node: { ...node, outcomes: { ...map, [outcome]: target } }, behaviorChanges: [] };
        }
        const created = {};
        const behaviorChanges = [];
        for (const o of outcomes) {
            if (o === outcome)
                created[o] = target;
            else if (isStop(o)) {
                created[o] = null;
                behaviorChanges.push({ slotKey: outcomeKey(o), before: 'stops_run', after: 'ends_branch' });
            }
        }
        return { node: { ...node, outcomes: created }, behaviorChanges };
    };
    /**
     * - `outcome_<x>`: with a map → `outcome:<x>`; without one only success-ish
     *   outcomes are recorded this way and they all left through `next`.
     * - `failed`: without a map this is how the runner records a stop outcome (and
     *   also a thrown executor) — both stop the run on this node, so it is counted
     *   on the `outcome:failed` stop slot. `skipped_no_adapter` stops are recorded
     *   as `failed` too; by status alone they land on the same slot, but the
     *   step's `result` names the outcome (`result.cancel` / `result.delete`,
     *   `registries.ts`) — given `result`, a `skipped_no_adapter` stop is placed on
     *   its own slot. With a map, `failed` can only be a thrown executor — not an
     *   exit.
     */
    const slotForStepStatus = (node, status, result) => {
        const hasMap = routedMap(node) !== undefined;
        if (status === 'failed') {
            if (hasMap)
                return null;
            const recorded = isRecord(result) ? (result.cancel ?? result.delete) : undefined;
            return typeof recorded === 'string' && recorded !== 'failed' && isStop(recorded)
                ? outcomeKey(recorded)
                : 'outcome:failed';
        }
        if (!status.startsWith('outcome_'))
            return null;
        const outcome = status.slice('outcome_'.length);
        if (!outcomes.includes(outcome))
            return null;
        if (hasMap)
            return outcomeKey(outcome);
        return isStop(outcome) ? outcomeKey(outcome) : 'next';
    };
    /** No map: the success outcomes bundled into «Далее», each pinnable on its own. */
    const splittableOutcomes = (node) => routedMap(node) !== undefined
        ? []
        : successOutcomes.map((o) => ({
            key: outcomeKey(o),
            labelRu: label(o),
            target: node.next,
            effect: 'falls_to_next',
            editable: true,
        }));
    return {
        outcomeKeys: outcomes.map(outcomeKey),
        slots,
        setSlotTarget,
        slotForStepStatus,
        optionalOutcomeMap: true,
        splittableOutcomes,
        writesContextKeys: noContextKeys,
    };
};
/** Stop outcomes of the back-compat (no `outcomes` map) regime — `runner.ts` `isStopOutcome`. */
const CANCEL_STOP_OUTCOMES = [
    'failed',
    'skipped_no_adapter',
];
const DELETE_STOP_OUTCOMES = ['failed'];
/**
 * Conversation statuses that still mean «someone is waiting» — setting one does
 * not settle a dialog parked by `collect_input`.
 */
const STILL_WAITING_STATUSES = new Set(['waiting', 'pending']);
// ---------------------------------------------------------------------------
// The map
export const NODE_SPECS = {
    user_identified: predicateSpec('user_identified'),
    has_active_subscription: predicateSpec('has_active_subscription'),
    field_match: {
        kind: 'condition',
        category: 'check',
        labelRu: pipelineNodeTypeLabelRu('field_match'),
        outcomeKeys: [],
        slots: conditionSlots,
        setSlotTarget: setConditionSlot,
        summaryRu: (node) => fieldMatchSummary(node),
        hintRu: null,
        create: (id) => ({
            id,
            kind: 'condition',
            type: 'field_match',
            source: 'payment',
            fieldPath: '',
            operator: 'exists',
            onTrue: null,
            onFalse: null,
        }),
        slotForStepStatus: conditionStepSlot,
        settlesDialog: never,
        ...PLAIN_OUTCOMES,
    },
    send_template_email: {
        kind: 'action',
        category: 'mail',
        labelRu: pipelineNodeTypeLabelRu('send_template_email'),
        outcomeKeys: [],
        slots: nextSlots,
        setSlotTarget: setNextSlot,
        summaryRu: (node, ctx) => withDelay(templateSummary(node.params.templateId, ctx), node.params.delay),
        hintRu: null,
        create: (id) => ({
            id,
            kind: 'action',
            type: 'send_template_email',
            params: { templateId: '' },
            next: null,
        }),
        slotForStepStatus: nextStepSlot,
        settlesDialog: never,
        ...PLAIN_OUTCOMES,
    },
    send_custom_email: {
        kind: 'action',
        category: 'mail',
        labelRu: pipelineNodeTypeLabelRu('send_custom_email'),
        outcomeKeys: [],
        slots: nextSlots,
        setSlotTarget: setNextSlot,
        summaryRu: (node) => {
            const subject = node.params.subject.trim();
            return withDelay(subject ? `Тема ${quote(clip(subject))}` : 'Тема не указана', node.params.delay);
        },
        hintRu: null,
        create: (id) => ({
            id,
            kind: 'action',
            type: 'send_custom_email',
            params: { subject: '', html: '' },
            next: null,
        }),
        slotForStepStatus: nextStepSlot,
        settlesDialog: never,
        ...PLAIN_OUTCOMES,
    },
    set_conversation_status: {
        kind: 'action',
        category: 'status',
        labelRu: pipelineNodeTypeLabelRu('set_conversation_status'),
        outcomeKeys: [],
        slots: nextSlots,
        setSlotTarget: setNextSlot,
        summaryRu: (node) => `Статус: ${statusLabelRu(node.params.status)}`,
        hintRu: null,
        create: (id) => ({
            id,
            kind: 'action',
            type: 'set_conversation_status',
            params: { status: 'resolved' },
            next: null,
        }),
        slotForStepStatus: nextStepSlot,
        settlesDialog: (node) => !STILL_WAITING_STATUSES.has(node.params.status),
        ...PLAIN_OUTCOMES,
    },
    cancel_subscription: {
        kind: 'action',
        category: 'subscription',
        labelRu: pipelineNodeTypeLabelRu('cancel_subscription'),
        ...routedSpecParts('cancel_subscription', CancelOutcomeSchema.options, CANCEL_STOP_OUTCOMES),
        summaryRu: () => null,
        hintRu: 'Отменяет подписку привязанного аккаунта',
        settlesDialog: never,
        // No `outcomes` map: the back-compat regime is the safe default — a failed
        // cancel STOPS the run instead of reaching a confirmation email.
        create: (id) => ({ id, kind: 'action', type: 'cancel_subscription', params: {}, next: null }),
    },
    delete_account: {
        kind: 'action',
        category: 'danger',
        labelRu: pipelineNodeTypeLabelRu('delete_account'),
        ...routedSpecParts('delete_account', DeleteAccountOutcomeSchema.options, DELETE_STOP_OUTCOMES),
        summaryRu: () => 'Необратимо',
        hintRu: null,
        settlesDialog: never,
        create: (id) => ({ id, kind: 'action', type: 'delete_account', params: {}, next: null }),
    },
    collect_input: {
        kind: 'action',
        category: 'wait',
        labelRu: pipelineNodeTypeLabelRu('collect_input'),
        ...fixedOutcomeSpecParts('collect_input', COLLECT_INPUT_OUTCOMES),
        summaryRu: (node, ctx) => `${templateSummary(node.params.questionTemplateId, ctx)}${LABEL_SEPARATOR}ждём ответ до ${durationRu(node.params.timeoutHours)}`,
        hintRu: null,
        create: (id) => ({
            id,
            kind: 'action',
            type: 'collect_input',
            // `timeoutHours` = the schema default; `extract` empty until the author
            // adds a field (the validator says «Добавьте хотя бы одно поле»).
            params: { questionTemplateId: '', extract: [], timeoutHours: 48 },
            outcomes: { collected: null, escalated: null },
        }),
        settlesDialog: never,
        optionalOutcomeMap: false,
        splittableOutcomes: noSplittable,
        writesContextKeys: (node) => node.params.extract,
    },
    resolve_identity: {
        kind: 'action',
        category: 'identity',
        labelRu: pipelineNodeTypeLabelRu('resolve_identity'),
        ...fixedOutcomeSpecParts('resolve_identity', RESOLVE_IDENTITY_OUTCOMES),
        summaryRu: (node) => node.params.sourceKey
            ? `По email из ${quote(node.params.sourceKey)}`
            : 'Поле с email не указано',
        hintRu: null,
        create: (id) => ({
            id,
            kind: 'action',
            type: 'resolve_identity',
            params: { by: 'email', sourceKey: '' },
            outcomes: { resolved: null, unresolved: null },
        }),
        settlesDialog: never,
        ...PLAIN_OUTCOMES,
    },
};
// ---------------------------------------------------------------------------
// Dispatch helpers — the public, node-shaped API. The casts are the same
// «TS cannot correlate a union member with its map entry» bridge as
// `emailIdentityOfNode`.
/** The spec of a node's type. */
export const nodeSpec = (node) => NODE_SPECS[node.type];
/** The spec of a node type. */
export const nodeSpecOf = (type) => NODE_SPECS[type];
/** Every node type, in palette order (conditions, then actions — the order `NODE_SPECS` lists them). */
export const NODE_TYPES = Object.keys(NODE_SPECS);
/** Exits of a node in display order. See {@link OutputSlot}. */
export const slotsOf = (node) => nodeSpec(node).slots(node);
/** Re-target one exit immutably. See the per-regime notes on {@link OutputSlot}. */
export const setSlotTarget = (node, key, target) => nodeSpec(node).setSlotTarget(node, key, target);
/**
 * Release a cancel/delete outcome back to the `next` fallback (delete its key
 * from the `outcomes` map). A no-op for any other node, an absent key, or a node
 * without a map. Never deletes the map itself: an empty map still means «every
 * outcome falls to `next`», which is NOT the no-map regime (where failures stop).
 *
 * @deprecated Use {@link clearOutcomeRouteReport}: same edit, plus the
 * behaviour change to show when a STOP outcome starts falling into «Далее».
 */
export const clearOutcomeRoute = (node, key) => {
    if (!nodeSpec(node).optionalOutcomeMap || !('outcomes' in node))
        return node;
    const map = node.outcomes;
    const outcome = parseOutcomeKey(key);
    if (map === undefined ||
        outcome === null ||
        !Object.prototype.hasOwnProperty.call(map, outcome)) {
        return node;
    }
    const rest = { ...map };
    delete rest[outcome];
    return { ...node, outcomes: rest };
};
/**
 * {@link clearOutcomeRoute} with its consequence spelled out. Releasing a STOP
 * outcome (cancel `failed` / `skipped_no_adapter`, delete `failed`) sends it
 * into `next` — the §2 п.4 trap — so it is reported as
 * `explicit | ends_branch → falls_to_next` for the UI to confirm. Releasing a
 * success outcome changes only the exit the author pointed at: no report.
 */
export const clearOutcomeRouteReport = (node, key) => {
    const cleared = clearOutcomeRoute(node, key);
    if (cleared === node)
        return { node, behaviorChanges: [] };
    const before = slotsOf(node).find((s) => s.key === key);
    const isStopOutcome = slotsOf(nodeSpec(node).create(node.id)).some((s) => s.key === key && s.effect === 'stops_run');
    if (!before || !isStopOutcome)
        return { node: cleared, behaviorChanges: [] };
    return {
        node: cleared,
        behaviorChanges: [
            {
                slotKey: key,
                before: before.target === null ? 'ends_branch' : 'explicit',
                after: 'falls_to_next',
            },
        ],
    };
};
/** Card summary of a node's parameters, or `null`. */
export const summaryRu = (node, ctx) => nodeSpec(node).summaryRu(node, ctx);
/**
 * The card's second line: the parameter summary, else the type's
 * {@link NodeSpec.hintRu}, else `null`.
 */
export const cardLineRu = (node, ctx) => summaryRu(node, ctx) ?? nodeSpec(node).hintRu;
/** A blank node of `type` with id `id`. */
export const createNode = (type, id) => nodeSpecOf(type).create(id);
/**
 * Exit a recorded step left through (for per-arrow run statistics). Takes the
 * NODE rather than just its type: for cancel/delete the same status maps to
 * different exits depending on whether the node has an `outcomes` map. Pass the
 * step's `result` when available — see {@link NodeSpec.slotForStepStatus}.
 */
export const stepStatusToSlot = (node, status, result) => nodeSpec(node).slotForStepStatus(node, status, result);
/** See {@link NodeSpec.splittableOutcomes}. */
export const splittableOutcomesOf = (node) => nodeSpec(node).splittableOutcomes(node);
/** See {@link NodeSpec.writesContextKeys}. */
export const contextKeysWrittenBy = (node) => nodeSpec(node).writesContextKeys(node);
/** See {@link NodeSpec.settlesDialog}. */
export const settlesDialog = (node) => nodeSpec(node).settlesDialog(node);
//# sourceMappingURL=node-spec.js.map